import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PageLayout from "../../components/PageLayout";
import StepFlowNavigate from "../../components/StepFlowNavigate";

import BrokerQuoteTransactionType from "./BrokerQuoteTransactionType";
import BrokerQuoteClientDetails from "./BrokerQuoteClientDetails";
import BrokerQuoteAnythingElse from "./BrokerQuoteAnythingElse";
import BrokerQuoteDetailsPage from "./BrokerQuoteDetailsPage";
import BrokerQuoteSent from "./BrokerQuoteSent";
import BrokerQuoteSubmission from "./BrokerQuoteSubmission";
import QuoteSellingProperty from "../QuoteCommon/QuoteSellingProperty";
import QuoteBuyingProperty from "../QuoteCommon/QuoteBuyingProperty";
import { BuyingMortgageLender } from "../QuoteCommon/MortgageLender";
import QuoteSorryPage from "../QuoteCommon/QuoteSorryPage";
import { selectGAClientId } from "../../features/analytics/analyticsSlice";
import useCampaignAttribution from "../../hooks/useCampaignAttribution";

const transactionTypeOptions = [
  { title: "Buying a property", value: "Buying" },
  { title: "Buying and selling a property", value: "BuyingAndSelling" },
];

const BrokerQuoteFlow = (props) => {
  const location = useLocation();
  const navigate = useNavigate();

  // FIXME: move this into components that use it once they've been converted
  // to functional components.
  const googleAnalyticsId = useSelector(selectGAClientId);
  const campaign = useCampaignAttribution();

  const { referralCode } = useParams();
  const [state, setState] = useState({
    loading: !!referralCode,
    error: null,
    referral: null,
    broker: null,
  });
  const [flowCompleted, setFlowCompleted] = useState(false);
  const [submittedFlowState, setSubmittedFlowState] = useState(null);

  const lastFetchedReferralCode = useRef(null);

  useEffect(() => {
    if (!flowCompleted && !!referralCode) {
      if (lastFetchedReferralCode.current === referralCode) return;
      lastFetchedReferralCode.current = referralCode;

      fetch(`/api/referralCode/${referralCode}?audience=referrer`)
        .then((resp) => resp.json())
        .then(
          (referralData) => {
            const referral = {
              referralCode: referralCode,
              discountAmount: referralData.discountAmount,
              discountMessage: referralData.discountMessage,
              quoteMessage: referralData.quoteMessage,
            };
            const broker = {
              emailAddress: referralData.broker?.emailAddress,
              fullName: referralData.broker?.fullName,
            };
            setState({
              loading: false,
              referral: referral,
              broker: broker,
            });
          },
          (err) => {
            console.error("Failed to load referral code", err);
            setState({
              loading: false,
            });
          },
        );
    }
  }, [referralCode, flowCompleted]);

  const shouldShowBuyingPropertyDetails = (transactionType) => {
    return transactionType === "Buying" || transactionType === "BuyingAndSelling";
  };

  const shouldShowSellingPropertyDetails = (transactionType) => {
    return transactionType === "BuyingAndSelling";
  };

  const shouldShowBuyingMortgageLender = (transactionDetails) => {
    return transactionDetails && !!transactionDetails.buyToLet;
  };

  const shouldShowQuoteSentToClient = (flowState) => {
    return flowState.progress === "sendClientQuote";
  };

  if (state.loading) {
    return null;
  }

  if (flowCompleted) {
    return (
      <PageLayout>
        <BrokerQuoteSent
          buyingDetails={submittedFlowState.buyingDetails}
          personalDetails={submittedFlowState.personalDetails}
          quoteDetails={submittedFlowState.quoteDetails}
          referral={submittedFlowState.referral}
          sellingDetails={submittedFlowState.sellingDetails}
          transactionType={submittedFlowState.transactionType}
          restartFlow={() => {
            setSubmittedFlowState(null);
            setFlowCompleted(false);
            navigate(location.pathname);
          }}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <StepFlowNavigate
        initialFlowState={{
          channel: "mortgage_broker_referrals",
          transactionType: null,
          personalDetails: null,
          referral: state.referral,
          broker: state.broker,
          sellingDetails: null,
          sellingRejectionReasons: [],
          buyingDetails: null,
          buyingRejectionReasons: [],
          additionalInformation: null,
          progress: null,
        }}
        steps={[
          {
            name: "Intro",
            slug: "intro",
            component: (flowState) => (
              <BrokerQuoteTransactionType
                transactionType={flowState.transactionType}
                transactionTypeOptions={transactionTypeOptions}
                referral={flowState.referral}
              />
            ),
          },
          {
            name: "Selling property details",
            slug: "sale-details",
            valid: (flowState) => shouldShowSellingPropertyDetails(flowState.transactionType),
            component: (flowState) => (
              <QuoteSellingProperty
                mode="referrer"
                transactionType={flowState.transactionType}
                sellingDetails={flowState.sellingDetails}
                defaultChannel={flowState.channel}
                referral={flowState.referral}
                referralLocked={true}
              />
            ),
          },
          {
            name: "Selling sorry page",
            slug: "sale-sorry",
            valid: (flowState) => flowState.sellingRejectionReasons.length > 0,
            component: (flowState) => (
              <QuoteSorryPage
                mode="referrer"
                transactionType={flowState.transactionType}
                rejectionReasons={flowState.sellingRejectionReasons}
                rejectQuote="selling"
              />
            ),
          },
          {
            name: "Buying property details",
            slug: "purchase-details",
            valid: (flowState) => shouldShowBuyingPropertyDetails(flowState.transactionType),
            component: (flowState) => (
              <QuoteBuyingProperty
                mode="referrer"
                transactionType={flowState.transactionType}
                buyingDetails={flowState.buyingDetails}
                defaultChannel={flowState.channel}
                referral={flowState.referral}
                referralLocked={true}
              />
            ),
          },
          {
            name: "Buying sorry page",
            slug: "purchase-sorry",
            valid: (flowState) => flowState.buyingRejectionReasons.length > 0,
            component: (flowState) => (
              <QuoteSorryPage
                mode="referrer"
                transactionType={flowState.transactionType}
                rejectionReasons={flowState.buyingRejectionReasons}
                rejectQuote="buying"
              />
            ),
          },
          {
            name: "Buying mortgage lender",
            slug: "purchase-mortgage-lender",
            valid: (flowState) => shouldShowBuyingMortgageLender(flowState.buyingDetails),
            component: (flowState) => <BuyingMortgageLender mode="direct" buyingDetails={flowState.buyingDetails} />,
          },
          {
            name: "Broker quote - client details page",
            slug: "client-details",
            component: (flowState) => (
              <BrokerQuoteClientDetails
                additionalInformation={flowState.additionalInformation}
                personalDetails={flowState.personalDetails}
                referral={flowState.referral}
              />
            ),
          },
          {
            name: "Broker quote - anything else",
            slug: "anything-else",
            component: (flowState) => (
              <BrokerQuoteAnythingElse
                transactionType={flowState.transactionType}
                channel={flowState.channel}
                personalDetails={flowState.personalDetails}
                additionalInformation={flowState.additionalInformation}
                showTermsLink={true}
                buttonText={"Continue"}
                referral={flowState.referral}
              />
            ),
          },
          {
            name: "Broker quote - quote details",
            slug: "quote-details",
            component: (flowState) => (
              <BrokerQuoteDetailsPage
                transactionType={flowState.transactionType}
                channel={flowState.channel}
                googleAnalyticsId={googleAnalyticsId}
                campaign={campaign}
                referral={flowState.referral}
                broker={flowState.broker}
                additionalInformation={flowState.additionalInformation}
                personalDetails={flowState.personalDetails}
                sellingDetails={flowState.sellingDetails}
                buyingDetails={flowState.buyingDetails}
                recipient="broker"
              />
            ),
          },
          {
            name: "Broker quote - send your client a quote",
            slug: "quote-sent",
            valid: (flowState) => shouldShowQuoteSentToClient(flowState),
            component: (flowState) => (
              <BrokerQuoteSubmission
                transactionType={flowState.transactionType}
                channel={flowState.channel}
                googleAnalyticsId={googleAnalyticsId}
                campaign={campaign}
                referral={flowState.referral}
                broker={flowState.broker}
                progress={flowState.progress}
                additionalInformation={flowState.additionalInformation}
                personalDetails={flowState.personalDetails}
                quoteDetails={flowState.quoteDetails}
                sellingDetails={flowState.sellingDetails}
                buyingDetails={flowState.buyingDetails}
                onFlowComplete={(quoteDetails) => {
                  setSubmittedFlowState({
                    ...flowState,
                    ...quoteDetails,
                  });
                  setFlowCompleted(true);
                }}
              />
            ),
          },
        ]}
      />
    </PageLayout>
  );
};

export default BrokerQuoteFlow;
